import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import usePermissions from '../../../../hooks/usePermissions'

import { insertIf } from '../../../../helpers/common'
import { getLineItemEditFormName } from './helpers'
import { getSelectedPlatform } from '../../../../helpers/handlePlatformSelection'

import { updateLineItem } from '../../../../modules/actions/lineItems'
import { openForm } from '../../../../modules/actions/forms'
import { copyAd } from '../../../../modules/actions/ads'
import { selectedAdForCopySelector } from '../../../../modules/selectors/ads'
import { selectedAdAccountsSelector } from '../../../../modules/selectors/adAccounts'
import { campaignSelector } from '../../../../modules/selectors/campaigns'

import { listItemChangeStatus } from '../../../../constants/other'
import {
  DV_360_PLATFORM,
  FACEBOOK_PLATFORM,
  GOOGLE_PLATFORM,
  TIKTOK_PLATFORM
} from '../../../../constants/selectLists/platformList'
import { CHANGE_LINE_ITEM_HISTORY } from '../../../../constants/forms'
import { HISTORY_META_EDIT_SECTION } from '../../../../constants/changeHistory'

const useGetLineItemDropdownOptions = handleDeleteRequest => {
  const dispatch = useDispatch()
  const permissions = usePermissions()

  const selectedPlatform = getSelectedPlatform()

  const selectedAdForCopy = useSelector(selectedAdForCopySelector)
  const { objective: campaignObjective } = useSelector(campaignSelector)
  const { [selectedPlatform]: selectedAdAccount = {} } = useSelector(selectedAdAccountsSelector)

  const { id: selectedAdAccountId } = selectedAdAccount

  const pasteAdHandler = useCallback(
    lineItemId => {
      const { id: adId, internal: isInternalAd } = selectedAdForCopy

      const data = {
        account: selectedAdAccountId,
        adset_id: lineItemId,
        ...(isInternalAd && { internal: true })
      }

      dispatch(copyAd(data, adId, selectedPlatform))
    },
    [dispatch, selectedAdForCopy, selectedAdAccountId, selectedPlatform]
  )

  return useCallback(
    lineItem => {
      const { id, status } = lineItem
      return [
        ...insertIf(permissions.can('update', 'LineItem'), {
          text: status === 'active' ? 'Pause' : 'Activate',
          onClickHandler: () => {
            const updatedLineItemData = {
              status: listItemChangeStatus[status].changeTo,
              account: selectedAdAccountId,
              ...(campaignObjective && { campaign_objective: campaignObjective }),
              meta: {
                [HISTORY_META_EDIT_SECTION]: 'Status'
              }
            }

            dispatch(updateLineItem(updatedLineItemData, id, selectedPlatform))
          }
        }),
        ...insertIf(
          (selectedPlatform === FACEBOOK_PLATFORM ||
            selectedPlatform === GOOGLE_PLATFORM ||
            selectedPlatform === TIKTOK_PLATFORM ||
            selectedPlatform === DV_360_PLATFORM) &&
            permissions.can('update', 'LineItem'),
          {
            text: 'Edit',
            onClickHandler: () => dispatch(openForm({ id, formName: getLineItemEditFormName(selectedPlatform) }))
          }
        ),
        ...insertIf(permissions.can('manage', 'LineItem'), {
          text: 'Delete',
          onClickHandler: () => {
            handleDeleteRequest({
              item: lineItem,
              platform: FACEBOOK_PLATFORM,
              account: selectedAdAccountId,
              campaignObjective
            })
          }
        }),
        // facebook only actions
        // Duplicate options is commented out because we need to check available budget or impressions before duplicating,
        // but we can't get available budget or impressions on campaign summary page
        // ...insertIf(selectedPlatform === FACEBOOK_PLATFORM && permissions.can('create', 'LineItem'), {
        //   text: 'Duplicate',
        //   onClickHandler: () => dispatch(openForm({ id, formName: LINE_ITEM_FACEBOOK_DUPLICATE }))
        // }),
        ...insertIf(selectedPlatform === FACEBOOK_PLATFORM && permissions.can('create', 'Ad') && !!selectedAdForCopy, {
          text: 'Paste ad',
          onClickHandler: () => pasteAdHandler(id)
        }),
        {
          text: 'Change History',
          onClickHandler: () => dispatch(openForm({ id, formName: CHANGE_LINE_ITEM_HISTORY }))
        }
      ]
    },
    [
      dispatch,
      permissions,
      selectedPlatform,
      selectedAdForCopy,
      selectedAdAccountId,
      campaignObjective,
      pasteAdHandler,
      handleDeleteRequest
    ]
  )
}

export default useGetLineItemDropdownOptions
